import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import DataTable, { Column } from '../../../components/DataTable/DataTable';
import ConfirmationDialog from '../../../components/ui/ConfirmationDialog';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { cn } from '../../../lib/utils';
import {
  Button,
  Select,
  TextInput,
  NumberInput,
} from '../../../components/ui';
import {
  createAccStatementLine,
  deleteAccStatementLine,
  syncAccStatementLinesFromCoa,
  updateAccStatementLine,
} from '../../../services/mutations';
import { ACCOUNTING_TABLE_DEFAULTS } from '../shared/typography';
import {
  AccAccountOption,
  AccStatementLine,
  StatementType,
  useAccAccountsForMapping,
  useAccStatementLines,
} from './useData';

const TAB_OPTIONS: { value: StatementType; label: string }[] = [
  { value: 'PL', label: 'Profit & loss' },
  { value: 'BS', label: 'Balance sheet' },
];

const accountDepth = (accountId: number, byId: Map<number, AccAccountOption>): number => {
  let depth = 0;
  let current = byId.get(accountId);
  while (current?.parentId != null && byId.has(current.parentId)) {
    depth++;
    current = byId.get(current.parentId);
  }
  return depth;
};

const signOptions = [
  { value: '1', label: '+ Normal' },
  { value: '-1', label: '− Invert' },
];

const LabelEditor: React.FC<{
  line: AccStatementLine;
  onSave: (label: string | null) => void;
}> = ({ line, onSave }) => {
  const [value, setValue] = useState(line.label ?? '');
  useEffect(() => {
    setValue(line.label ?? '');
  }, [line.id, line.label]);
  return (
    <TextInput
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        const next = value.trim() || null;
        if (next !== (line.label?.trim() || null)) onSave(next);
      }}
      placeholder={line.account?.name ?? 'Label on report'}
      size="sm"
      showClearButton={false}
    />
  );
};

const SortOrderEditor: React.FC<{
  line: AccStatementLine;
  onSave: (sortOrder: number) => void;
}> = ({ line, onSave }) => {
  const [value, setValue] = useState(String(line.sortOrder));
  useEffect(() => {
    setValue(String(line.sortOrder));
  }, [line.id, line.sortOrder]);
  return (
    <NumberInput
      value={value}
      onChange={(e) => setValue(e.target.value == null ? '' : String(e.target.value))}
      onBlur={() => {
        const next = Number(value);
        if (Number.isFinite(next) && next !== line.sortOrder) onSave(Math.max(0, Math.trunc(next)));
      }}
      min={0}
      step={1}
      allowDecimals={false}
      showClearButton={false}
      size="sm"
    />
  );
};

export const StatementLines: React.FC = () => {
  const { colors } = useTheme();
  const { can } = useAuth();
  const canEdit = can('acc_accounts', 'edit');
  const [searchParams, setSearchParams] = useSearchParams();

  const initialType = searchParams.get('type') === 'BS' ? 'BS' : 'PL';
  const [statementType, setStatementType] = useState<StatementType>(initialType);
  const [syncing, setSyncing] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [addAccountId, setAddAccountId] = useState('');
  const [adding, setAdding] = useState(false);

  const { data: lines, isLoading, mutate } = useAccStatementLines(statementType);
  const { accounts, isLoading: accountsLoading } = useAccAccountsForMapping();

  useEffect(() => {
    const type = searchParams.get('type') === 'BS' ? 'BS' : 'PL';
    setStatementType(type);
  }, [searchParams]);

  const selectStatementType = (type: StatementType) => {
    setStatementType(type);
    setSearchParams(type === 'BS' ? { type: 'BS' } : {}, { replace: true });
  };

  const accountById = useMemo(() => new Map(accounts.map((a) => [a.id, a])), [accounts]);

  const mappedAccountIds = useMemo(() => new Set(lines.map((l) => l.accountId)), [lines]);

  const addableAccounts = useMemo(() => {
    return accounts
      .filter((a) => accountDepth(a.id, accountById) <= 1)
      .filter((a) => !mappedAccountIds.has(a.id))
      .sort((a, b) => a.combLevel.localeCompare(b.combLevel))
      .map((a) => ({ value: String(a.id), label: a.name }));
  }, [accounts, accountById, mappedAccountIds]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await syncAccStatementLinesFromCoa();
      toast.success(
        result.created > 0
          ? `Added ${result.created} mapping${result.created === 1 ? '' : 's'} from chart of accounts.`
          : 'No new mappings — chart of accounts is already in sync.',
      );
      await mutate();
    } catch {
      toast.error('Could not sync from chart of accounts.');
    } finally {
      setSyncing(false);
    }
  };

  const saveField = useCallback(
    async (line: AccStatementLine, patch: Partial<AccStatementLine>) => {
      try {
        await updateAccStatementLine(line.id, patch);
        await mutate();
      } catch {
        toast.error('Could not save mapping.');
      }
    },
    [mutate],
  );

  const handleAdd = async () => {
    if (!addAccountId) return;
    setAdding(true);
    try {
      await createAccStatementLine({
        statementType,
        accountId: Number(addAccountId),
      });
      setAddAccountId('');
      toast.success('Mapping added.');
      await mutate();
    } catch {
      toast.error('Could not add mapping.');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteAccStatementLine(id);
      toast.success('Mapping removed.');
      setDeleteId(null);
      await mutate();
    } catch {
      toast.error('Could not delete mapping.');
    }
  };

  const columns = useMemo((): Column<AccStatementLine>[] => [
    {
      key: 'sortOrder',
      header: 'Order',
      width: '5.5rem',
      render: (_v, row) =>
        canEdit ? (
          <SortOrderEditor line={row} onSave={(sortOrder) => void saveField(row, { sortOrder })} />
        ) : (
          row.sortOrder
        ),
    },
    {
      key: 'account',
      header: 'COA code',
      sortable: true,
      render: (_v, row) => (
        <span className="tabular-nums">{row.account?.combLevel ?? row.accountId}</span>
      ),
    },
    {
      key: 'accountName',
      header: 'Account name',
      render: (_v, row) => row.account?.name ?? '—',
    },
    {
      key: 'label',
      header: 'Statement label',
      render: (_v, row) =>
        canEdit ? (
          <LabelEditor line={row} onSave={(label) => void saveField(row, { label })} />
        ) : (
          row.label ?? row.account?.name ?? '—'
        ),
    },
    {
      key: 'sign',
      header: 'Sign',
      width: '9rem',
      render: (_v, row) =>
        canEdit ? (
          <Select
            value={String(row.sign)}
            onChange={(e) => void saveField(row, { sign: Number(e.target.value) })}
            options={signOptions}
            showClearButton={false}
          />
        ) : (
          row.sign === -1 ? 'Invert' : 'Normal'
        ),
    },
    ...(canEdit
      ? [{
          key: 'actions',
          header: '',
          width: '4rem',
          align: 'right' as const,
          render: (_v: unknown, row: AccStatementLine) => (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-red-600 hover:text-red-700"
              onClick={() => setDeleteId(row.id)}
            >
              Remove
            </Button>
          ),
        }]
      : []),
  ], [canEdit, saveField]);

  return (
    <div className="flex flex-col h-full min-h-0 p-4 gap-3">
      <div
        className={cn(
          'flex flex-1 min-h-0 flex-col overflow-hidden rounded-xl border shadow-sm',
          colors.surface,
          colors.border,
        )}
      >
        <div className={cn('flex-shrink-0 border-b px-4 py-3 space-y-3', colors.border, colors.surfaceHover)}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 space-y-1">
              <h1 className={cn('text-base font-semibold', colors.text)}>Statement line mapping</h1>
              <p className={cn('text-sm max-w-2xl', colors.textMuted)}>
                Map chart-of-accounts headers (levels 1–2) to P&amp;L and balance sheet lines.
                Amounts roll up from posting accounts beneath each mapped node.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <Link
                to={`/accounting/reports/${statementType === 'PL' ? 'profit-and-loss' : 'balance-sheet'}`}
                className="text-sm text-indigo-600 hover:underline"
              >
                View report
              </Link>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className={cn('inline-flex rounded-lg border p-0.5', colors.border)}>
              {TAB_OPTIONS.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => selectStatementType(tab.value)}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                    statementType === tab.value
                      ? cn(colors.primary, colors.primaryText)
                      : cn(colors.textMuted, 'hover:opacity-80'),
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {canEdit && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleSync}
                  disabled={syncing}
                  loading={syncing}
                >
                  Sync from COA
                </Button>
                <div className="flex items-center gap-2 min-w-[16rem] flex-1 max-w-md">
                  <Select
                    value={addAccountId}
                    onChange={(e) => setAddAccountId(e.target.value)}
                    options={[{ value: '', label: 'Add COA header…' }, ...addableAccounts]}
                    showClearButton={false}
                    disabled={accountsLoading || addableAccounts.length === 0}
                  />
                  <Button
                    type="button"
                    size="sm"
                    disabled={!addAccountId || adding}
                    loading={adding}
                    onClick={handleAdd}
                  >
                    Add
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden">
          <DataTable
            {...ACCOUNTING_TABLE_DEFAULTS}
            data={lines}
            columns={columns}
            loading={isLoading}
            keyExtractor={(row) => String(row.id)}
            emptyMessage={
              canEdit
                ? 'No mappings yet. Click “Sync from COA” to seed from your chart of accounts.'
                : 'No statement line mappings configured.'
            }
            stickyHeader
          />
        </div>
      </div>

      {deleteId != null && (
        <ConfirmationDialog
          isOpen
          title="Remove mapping"
          message="Remove this account from the statement layout? The report will no longer include this line until you add it again."
          confirmText="Remove"
          cancelText="Cancel"
          confirmVariant="danger"
          onConfirm={() => handleDelete(deleteId)}
          onClose={() => setDeleteId(null)}
        />
      )}
    </div>
  );
};
